import React from 'react';

import { Nullable } from '@clearverify/types';

import { useGetPeoStaffAccessInfoQuery } from 'src/api';

import { useCurrentUserPermissions } from '../user';

export const usePeoStaffAccesses = (peoStaffId?: Nullable<string>) => {
  const { hasPlatformRoles, isPeoPayrollProviderUser, isPeoSuperAdmin } =
    useCurrentUserPermissions();

  // Load for everyone who can view the access-restriction section (see
  // canSetupAccessRestriction in PeoStaffViewDetailsModalForm). Previously this only
  // loaded for platform admins / super admins, so a Platform Manager or Payroll-Provider
  // user got empty initialValues and silently wiped the lists on save.
  const canViewAccessRestriction = hasPlatformRoles || isPeoPayrollProviderUser || isPeoSuperAdmin;

  const { data, loading } = useGetPeoStaffAccessInfoQuery({
    variables: {
      id: peoStaffId || '',
    },
    skip: !canViewAccessRestriction || !peoStaffId,
  });

  const accessRestrictionMode = React.useMemo(
    () => data?.getPeoStaffAccessInfo?.accessRestrictionMode,
    [data?.getPeoStaffAccessInfo?.accessRestrictionMode],
  );

  const peoStaffBlacklists = React.useMemo(() => {
    if (!data?.getPeoStaffAccessInfo) return null;
    return {
      blacklistedEmployers: data?.getPeoStaffAccessInfo?.blacklistedEmployersList,
      blacklistedEmployees: data?.getPeoStaffAccessInfo?.blacklistedEmployeesList,
    };
  }, [data?.getPeoStaffAccessInfo]);

  const peoStaffWhitelists = React.useMemo(() => {
    if (!data?.getPeoStaffAccessInfo?.whitelistedEmployersList) return null;
    return {
      whitelistedEmployers: data?.getPeoStaffAccessInfo?.whitelistedEmployersList,
    };
  }, [data?.getPeoStaffAccessInfo?.whitelistedEmployersList]);

  return {
    accessRestrictionMode,
    peoStaffBlacklists,
    peoStaffWhitelists,
    loading,
  };
};
